/**
 * harbourForms.js
 * ---------------
 * Pure helpers for the Harbour school-forms surfaces: which forms belong to
 * which pathway homepage, their routes, display titles, sequential locking,
 * and honest per-form counts derived from the LIVE schema (never hardcoded —
 * the schema is the single source of truth for question content).
 */

import {
  HFP_FORM_TITLES,
  HFP_V4_PORTAL_FORM_KEY,
  effectiveBaseReferralForms,
  hfpFormKeysForRecord,
} from './referralStatus';
import { isSectionVisibleForRole, isQuestionVisible, isConditionalMet } from './schemaEvaluator';

/** formKey → the existing form route slug under /rms/patient/:id/forms/. */
export const HARBOUR_FORM_SLUGS = {
  requestForReferral: 'request-for-referral',
  reasonForReferral: 'reason-for-referral',
  educationHistory: 'education-history',
  snapIV26: 'snap-iv-26',
  hfpV2ExecutiveFunction: 'hfp-v2-executive-function',
  hfpV2MotorPhysical: 'hfp-v2-motor-physical',
  hfpV2SpeechLanguageCommunication: 'hfp-v2-speech-language-communication',
  hfpV2SocialEmotional: 'hfp-v2-social-emotional',
  hfpV2Sensory: 'hfp-v2-sensory',
  hfpV2GlobalParticipation: 'hfp-v2-global-participation',
  hfpV2EnvironmentalFactors: 'hfp-v2-environmental-factors',
  hfpV3ExecutiveFunction: 'hfp-v3-executive-function',
  hfpV3MotorPhysical: 'hfp-v3-motor-physical',
  hfpV3SpeechLanguageCommunication: 'hfp-v3-speech-language-communication',
  hfpV3SocialEmotional: 'hfp-v3-social-emotional',
  hfpV3Sensory: 'hfp-v3-sensory',
  hfpV3GlobalParticipation: 'hfp-v3-global-participation',
  hfpV3EnvironmentalFactors: 'hfp-v3-environmental-factors',
};

export function harbourFormRoute(patientRecordId, formKey) {
  // The v4 strengths and needs form is its own page, not a schema form (TDI-525).
  if (formKey === HFP_V4_PORTAL_FORM_KEY) return `/rms/patient/${encodeURIComponent(patientRecordId)}/hfp-v4`;
  const slug = HARBOUR_FORM_SLUGS[formKey];
  if (!slug) return null;
  return `/rms/patient/${encodeURIComponent(patientRecordId)}/forms/${slug}`;
}

/** Display titles for the referral base forms (schema titles are authoritative
 * once loaded; these cover the list before schemas arrive). */
const BASE_FORM_TITLES = {
  requestForReferral: 'Request for Referral',
  reasonForReferral: 'Reason for Referral',
  educationHistory: 'Education History',
  snapIV26: 'ADHD Symptoms Scale',
};

/** Display titles for the guardian-only triage forms (schema titles win once
 * loaded). Shared by the family forms homepage and the confirm-and-submit
 * screen so the two never list a form under different names. */
export const TRIAGE_FORM_TITLES = {
  theirEarlyLife: 'Their Early Life',
  theirFamilyAndHomeLife: 'Their Family and Home Life',
  theirLifestyle: 'Their Lifestyle',
  theirHistoryWithTheLaw: 'Their History with the Law',
  theirFamilyHistory: 'Their Family History',
  theirMedicalHistory: 'Their Medical History',
  theirMentalHealthHistory: 'Their Mental Health History',
};

/**
 * The seven HFP v3 forms as the portals name them: the live schemas' own
 * plain-English titles (3.1.0 on dev and staging). Used before a schema has
 * loaded, so a forms homepage never opens with one set of names and switches
 * to another. Clinical screens keep the domain names in HFP_FORM_TITLES.
 */
export const PORTAL_HFP_FORM_TITLES = {
  hfpV3ExecutiveFunction: 'Thinking and organising',
  hfpV3MotorPhysical: 'Movement and coordination',
  hfpV3SpeechLanguageCommunication: 'Talking and understanding',
  hfpV3SocialEmotional: 'Feelings and relationships',
  hfpV3Sensory: 'Senses',
  hfpV3GlobalParticipation: 'Everyday life',
  hfpV3EnvironmentalFactors: 'Things around them',
  [HFP_V4_PORTAL_FORM_KEY]: 'Strengths and needs profile',
};

export function harbourFormTitle(formKey, schema) {
  return (schema && schema.title)
    || PORTAL_HFP_FORM_TITLES[formKey]
    || HFP_FORM_TITLES[formKey]
    || BASE_FORM_TITLES[formKey]
    || TRIAGE_FORM_TITLES[formKey]
    || formKey;
}

/**
 * The form keys for a Harbour school-forms homepage.
 *
 * SEN pathway: the seven HFP school forms.
 * Referral pathway: the base referral forms for the concern, and nothing
 * else — every pupil, every phase, on the SEND pathway or not.
 *
 * The HFP set comes from the record's instrument (hfpFormKeysForRecord,
 * TDI-523): its pin once a form has opened, else the organisation's setting
 * when the caller passes `orgSetting`, else the pathway default (v3 for a
 * Harbour pupil, v2 otherwise). That choice now only affects the SEN
 * pathway, since it is the only one asking for those forms.
 *
 * @param {'sen'|'referral'} pathway
 * @param {object} [opts]
 * @param {object|null} [opts.patient] - patientRecords doc data
 * @param {string|null} [opts.intakeConcern]
 * @param {'v2'|'v3'|'v4'|null} [opts.orgSetting] - the organisation's HFP instrument setting
 * @returns {string[]}
 */
export function harbourFormKeysFor(pathway, { patient = null, intakeConcern, orgSetting = null } = {}) {
  // The strengths and needs forms are the SEND pathway's, and only its.
  // Diana's homepages are four school forms and eleven parent forms on a
  // referral, with no HFP on either, and Tom's 27 October ruling makes the two
  // pathways separate with no exception for a child on one of them. Until 28
  // October a referral-only child was given the HFP set here as well.
  if (pathway === 'sen') return [...hfpFormKeysForRecord(patient, orgSetting)];
  return effectiveBaseReferralForms(intakeConcern);
}

/**
 * Count the questions a role will actually be asked by a schema: questions in
 * sections visible to the role, excluding pure info panels and any question
 * that only appears after a particular answer (a follow-up's visibleWhen, or
 * an HFP conditionalOn modifier). Those gates are evaluated exactly as the
 * form evaluates them, with no answers yet, so the homepage never quotes
 * more questions than the form will ask up front.
 *
 * `recordContext` carries the child's sex and school phase
 * ({ subjectSex, schoolPhase }, from recordFormContext) so a form that leaves
 * parts out for a boy or a primary-age child quotes the shorter count
 * (TDI-708, TDI-709). Without it every part is counted.
 */
export function countSchemaQuestions(schema, role = 'school_user', initiatedBy = 'school', recordContext = null) {
  if (!schema || !Array.isArray(schema.sections)) return null;
  // The same context the form passes its evaluator (useRmsFlowContext).
  const context = {
    pathway: 'cyp',
    initiatedBy: initiatedBy || 'school',
    currentUser: { role },
    ...(recordContext || {}),
  };
  let count = 0;
  for (const section of schema.sections) {
    let visible = true;
    try {
      visible = recordContext
        ? isQuestionVisible({}, section.visibleWhen, context)
        : isSectionVisibleForRole(section, role, initiatedBy);
    } catch (_) {
      visible = true;
    }
    if (!visible) continue;
    for (const q of section.questions || []) {
      if (!q || q.type === 'info') continue;
      let asked = true;
      try {
        asked = isQuestionVisible({}, q.visibleWhen, context) && isConditionalMet({}, q);
      } catch (_) {
        asked = true;
      }
      if (asked) count += 1;
    }
  }
  return count || null;
}

/**
 * Referral-journey questions the Harbour "Add to referral pathway" wizard
 * asks once, at pathway start (rmsHarbourStartReferral). The server prefills
 * them on the school's forms (rms_forms._harbour_referral_prefill) and the
 * forms never ask them again (RmsPostFormPage harbourWizardHiddenQuestionIds):
 * the informant and told-to-expect-email on Request for Referral, and the
 * "what is the referral for" choice (main_reason) on Reason for Referral.
 */
export const HARBOUR_WIZARD_ANSWERED_QUESTION_IDS = [
  'guardian_told_to_expect_email',
  'guardian_nomination',
  'main_reason',
];

/**
 * How many of a form's questions the referral wizard has already answered for
 * this record, so the school's forms homepage quotes the same count the form
 * itself will ask. Non-zero only for the school's Request for Referral and
 * Reason for Referral on a Harbour pupil whose referral was started through
 * the wizard.
 *
 * @param {string} formKey
 * @param {object|null} patient - patientRecords doc data
 * @param {object|null} schema
 * @returns {number}
 */
export function harbourWizardAnsweredCount(formKey, patient, schema) {
  if (!schema || !Array.isArray(schema.sections)) return 0;
  if (!(patient && patient.harbour && patient.harbour.pupil)) return 0;
  const rp = patient.referralPathway || {};
  const answered = [];
  if (formKey === 'requestForReferral') {
    if (rp.guardianToldToExpectEmail) answered.push('guardian_told_to_expect_email');
    if (rp.informant && rp.informant.email) answered.push('guardian_nomination');
  } else if (formKey === 'reasonForReferral') {
    if (['adhd', 'asd', 'both'].includes(rp.referralFor)) answered.push('main_reason');
  }
  if (!answered.length) return 0;
  let count = 0;
  for (const section of schema.sections) {
    for (const q of section.questions || []) {
      if (q && answered.includes(q.questionId)) count += 1;
    }
  }
  return count;
}

/** Honest time estimate from the schema, falling back to ~30s per question. */
export function estimatedMinutes(schema, questionCount) {
  const metaMinutes = schema && schema.meta && schema.meta.estimatedMinutes;
  if (Number.isFinite(metaMinutes) && metaMinutes > 0) return Math.round(metaMinutes);
  if (Number.isFinite(questionCount) && questionCount > 0) {
    return Math.max(1, Math.round(questionCount * 0.5));
  }
  return null;
}

/**
 * Build the homepage row models with the sequential lock:
 * "Answer one form at a time. The next one opens when you finish the one
 * before. Finished forms stay open, so you can change an answer at any time."
 *
 * @param {string[]} formKeys - in homepage order
 * @param {Record<string, number>} formProgress - role-suffixed instance map as
 *   built by buildFormProgressFromSnapshot: percent per instance, where 100
 *   means SUBMITTED (drafts are capped at 99 by completionPercent).
 * @param {'school_user'} role
 * @param {Record<string, object>} [heldBy] - formKey -> the teacher who fills
 *   that form in (TDI-618, IN-13). A form with a teacher is theirs: it is
 *   never open to the viewer, and the lock runs over the viewer's own forms
 *   only, so a teacher's form neither blocks nor waits on the SENCO's.
 * @returns {Array<{ formKey, status: 'done'|'inProgress'|'notStarted', open: boolean, heldBy: object|null }>}
 */
export function buildHomeRows(formKeys, formProgress = {}, role = 'school_user', heldBy = {}) {
  const states = formKeys.map((formKey) => {
    const raw = formProgress[`${formKey}_${role}`] ?? formProgress[formKey];
    const percent = Number.isFinite(Number(raw)) ? Number(raw) : 0;
    return {
      formKey,
      status: percent >= 100 ? 'done' : percent > 0 ? 'inProgress' : 'notStarted',
      heldBy: (heldBy && heldBy[formKey]) || null,
    };
  });
  const own = states.filter((s) => !s.heldBy);
  const firstNotDone = own.find((s) => s.status !== 'done');
  return states.map((s) => ({
    ...s,
    open: !s.heldBy
      && (s.status !== 'notStarted' || s === firstNotDone || !firstNotDone),
  }));
}

/**
 * Who completes which school forms, and how far each person has got
 * (TDI-489, design T-15). The SENCO keeps every school form they have not
 * handed to a teacher, so their own line shrinks as they allocate; a teacher
 * holding no forms is not listed at all.
 *
 * @param {Object} args
 * @param {string[]} args.formKeys - the school forms this pathway asks for
 * @param {Object} args.formProgress - percent per instance, 100 meaning submitted
 * @param {Array} args.teachers - participants with an `allocations` map keyed by form key
 * @returns {Array<{ key: string, label: string, completed: number, total: number }>}
 */
export function schoolFormsByPerson({ formKeys = [], formProgress = {}, teachers = [] }) {
  const holder = new Map();
  for (const teacher of teachers) {
    for (const formKey of Object.keys(teacher.allocations || {})) {
      if ((teacher.allocations || {})[formKey]) holder.set(formKey, teacher);
    }
  }
  const isDone = (formKey) => {
    const raw = formProgress[`${formKey}_school_user`] ?? formProgress[formKey];
    return Number.isFinite(Number(raw)) && Number(raw) >= 100;
  };
  const countOf = (keys) => ({ completed: keys.filter(isDone).length, total: keys.length });

  const rows = [];
  const mine = formKeys.filter((formKey) => !holder.has(formKey));
  rows.push({ key: 'you', label: 'Your forms', ...countOf(mine) });

  for (const teacher of teachers) {
    const keys = formKeys.filter((formKey) => holder.get(formKey) === teacher);
    const name = teacher.firstName
      || [teacher.firstName, teacher.lastName].filter(Boolean).join(' ')
      || teacher.email
      || 'This teacher';
    rows.push({
      key: teacher.participantId || teacher.id || teacher.email || name,
      label: `${name}'s forms`,
      ...countOf(keys),
    });
  }
  // A person with nothing to do is not a line on the record.
  return rows.filter((row) => row.total > 0);
}
